import { BaseTableAdapter } from '../../domain/query/base-adapter';
import type { PageResult } from '../../domain/query/paging';
import type { Capabilities, TableFetchParams } from '../../ports/table-adapter';
import { buildOrderBy } from './order';
import { buildWhere, type Where } from './where';

export interface QueryOptions {
    query: unknown;
    variables?: Record<string, unknown>;
    fetchPolicy?: string;
    context?: Record<string, unknown>;
}

export interface QueryClient {
    // eslint-disable-next-line typescript/method-signature-style -- method syntax keeps the parameter bivariant, so a concrete client (ApolloClient) satisfies this loose port
    query(opts: QueryOptions): Promise<{ data: unknown }>;
}

export interface PageInfo {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    startCursor?: string | null;
    endCursor?: string | null;
}

export interface ConnectionEdge<TNode> {
    node?: TNode | null;
    cursor?: string;
}

export interface Connection<TNode> {
    totalCount?: number;
    pageInfo?: PageInfo;
    edges?: (ConnectionEdge<TNode> | null)[] | null;
}

export type GraphQLFetchPolicy = 'cache-first' | 'network-only' | 'no-cache';

export type ColumnVisibilityVariables<TVariables> = Record<
    string,
    keyof TVariables & string
>;

export interface GraphQLConfig<TData, TNode, TRow = TNode, TCtx = void> {
    client: QueryClient;
    query: unknown;
    variables?: Record<string, unknown>;
    extractConnection: (data: TData) => Connection<TNode>;
    mapRow?: (node: TNode, ctx: TCtx) => TRow;
    context?: () => Promise<TCtx>;
    searchFieldPaths?: string[];
    pageSize?: number;
    defaultFetchPolicy?: GraphQLFetchPolicy;
    visibilityVariables?: Record<string, string>;
}

export class GraphQLTableAdapter<
    TData,
    TNode,
    TRow = TNode,
    TCtx = void,
> extends BaseTableAdapter<TRow> {
    #cfg: GraphQLConfig<TData, TNode, TRow, TCtx>;
    #ctx?: Promise<TCtx>;
    #visibleFields?: Set<string>;

    constructor(cfg: GraphQLConfig<TData, TNode, TRow, TCtx>) {
        super(cfg.pageSize);
        this.#cfg = cfg;
    }

    setVisibleFields(fields: string[]): void {
        this.#visibleFields = new Set(fields);
    }

    #isFieldOrDescendantVisible(key: string): boolean {
        const visibleFields = this.#visibleFields;
        if (!visibleFields) {
            return false;
        }
        if (visibleFields.has(key)) {
            return true;
        }
        const prefix = `${key}.`;
        for (const field of visibleFields) {
            if (field.startsWith(prefix)) {
                return true;
            }
        }
        return false;
    }

    protected caps(): Capabilities {
        return { totalCount: true, sort: true, filter: true };
    }

    next(): void {
        this.moveNext();
    }
    prev(): void {
        this.movePrev();
    }
    first(): void {
        this.moveFirst();
    }
    last(): void {
        this.moveLast();
    }

    protected searchFieldPaths(): string[] {
        return this.#cfg.searchFieldPaths ?? [];
    }

    protected async fetchPage(
        params: TableFetchParams,
    ): Promise<PageResult<TRow>> {
        const cfg = this.#cfg;
        this.#ctx ??= cfg.context
            ? cfg.context()
            : Promise.resolve(undefined as TCtx);
        const ctx = await this.#ctx;

        const { page, pageSize, sort, filters, search, signal } = params;
        const variables: Record<string, unknown> = { ...cfg.variables };

        switch (page.kind) {
            case 'first':
                variables.first = pageSize;
                break;
            case 'after':
                variables.first = pageSize;
                variables.after = page.token;
                break;
            case 'last':
                variables.last = pageSize;
                break;
            case 'before':
                variables.last = pageSize;
                variables.before = page.token;
                break;
            case 'at':
                throw new Error(
                    'GraphQLTableAdapter: randomAccess is false, so {kind:"at"} must never reach fetchPage',
                );
        }

        const orderBy = buildOrderBy(sort);
        if (orderBy) variables.orderBy = orderBy;

        const where = buildWhere({
            base: cfg.variables?.where as Where | undefined,
            filters,
            search,
            searchFieldPaths: this.searchFieldPaths(),
        });
        if (where) variables.where = where;

        if (this.#visibleFields && cfg.visibilityVariables) {
            const visibilityByVar: Record<string, boolean> = {};
            for (const [field, varName] of Object.entries(
                cfg.visibilityVariables,
            )) {
                const isVisible = this.#isFieldOrDescendantVisible(field);
                visibilityByVar[varName] =
                    Boolean(visibilityByVar[varName]) || isVisible;
            }
            for (const [varName, isVisible] of Object.entries(
                visibilityByVar,
            )) {
                variables[varName] = isVisible;
            }
        }

        const context: Record<string, unknown> = {
            fetchOptions: { signal },
        };
        if (params.force) context.queryDeduplication = false;
        const fetchPolicy = params.force
            ? 'network-only'
            : cfg.defaultFetchPolicy ?? 'cache-first';
        const { data } = await cfg.client.query({
            query: cfg.query,
            variables,
            fetchPolicy,
            context,
        });

        const connection = cfg.extractConnection(data as TData);
        const rows: TRow[] = [];
        for (const edge of connection.edges ?? []) {
            const node = edge?.node;
            if (!node) continue;
            rows.push(
                cfg.mapRow ? cfg.mapRow(node, ctx) : (node as unknown as TRow),
            );
        }

        const pageInfo = connection.pageInfo;
        let hasNext: boolean | undefined;
        let hasPrev: boolean | undefined;
        if (pageInfo) {
            hasNext = pageInfo.hasNextPage;
            hasPrev = pageInfo.hasPreviousPage;
        } else {
            hasNext = rows.length === pageSize;
        }

        return {
            rows,
            totalCount: connection.totalCount,
            nextToken: pageInfo?.endCursor,
            prevToken: pageInfo?.startCursor,
            hasNext,
            hasPrev,
        };
    }
}
